/**
 * How does it feel? – wheel label, popup, and variant-driven effect highlighting.
 *
 * When on a product page the flavor tabs are the product variants. Clicking a tab
 * (or switching the main variant picker) highlights the effects that variant
 * helps with, and opens the first matching popup.
 */
(function () {
  var metafieldsCache = null;

  function getJsonUrl() {
    var meta = document.querySelector('meta[name="soul-metafields-url"]');
    return meta ? meta.getAttribute('content') : null;
  }

  function buildTitleVariantIndex(data) {
    if (!data || !data.by_variant_id || data._by_title_variant) return;
    var idx = {};
    Object.keys(data.by_variant_id).forEach(function (key) {
      var e = data.by_variant_id[key];
      if (e.product_title && e.variant_name) {
        idx[e.product_title + '::' + e.variant_name] = e;
      }
    });
    data._by_title_variant = idx;
  }

  function loadData() {
    if (metafieldsCache) return Promise.resolve(metafieldsCache);
    var url = getJsonUrl();
    if (!url) return Promise.resolve(null);
    return fetch(url)
      .then(function (res) { return res.json(); })
      .then(function (data) {
        buildTitleVariantIndex(data);
        metafieldsCache = data;
        return data;
      });
  }

  function getHelpsWithForVariant(data, variantId) {
    var entry = data && data.by_variant_id && data.by_variant_id[String(variantId)];
    return (entry && entry.helps_with) ? entry.helps_with : null;
  }

  // Look up helps_with by product_id — used for single-dose products whose
  // variant_id fields are empty in the JSON (e.g. Sleepi Gummies).
  // Returns the helps_with of the first matching entry found.
  function getHelpsWithForProduct(data, productId) {
    if (!data || !data.by_variant_id || !productId) return null;
    var ids = Object.keys(data.by_variant_id);
    for (var i = 0; i < ids.length; i++) {
      var entry = data.by_variant_id[ids[i]];
      if (String(entry.product_id) === String(productId) && entry.helps_with && entry.helps_with.length) {
        return entry.helps_with;
      }
    }
    return null;
  }

  // Look up helps_with by product title + variant name (most precise title-based fallback).
  function getHelpsWithByTitleAndVariantName(data, productTitle, variantName) {
    if (!data || !data._by_title_variant || !productTitle || !variantName) return null;
    var entry = data._by_title_variant[productTitle + '::' + variantName];
    return (entry && entry.helps_with) ? entry.helps_with : null;
  }

  // Look up helps_with by matching variant_name text (case-insensitive, last-resort fallback).
  function getHelpsWithByName(data, name) {
    if (!data || !data.by_variant_id) return null;
    var needle = name.trim().toLowerCase();
    var ids = Object.keys(data.by_variant_id);
    for (var i = 0; i < ids.length; i++) {
      var entry = data.by_variant_id[ids[i]];
      if (entry.variant_name && entry.variant_name.trim().toLowerCase() === needle) {
        return entry.helps_with || null;
      }
    }
    return null;
  }

  // Resolve helps_with using all available fallbacks in priority order.
  function resolveHelpsWith(data, variantId, productId, productTitle, variantName) {
    return getHelpsWithForVariant(data, variantId)
        || (productTitle && variantName && getHelpsWithByTitleAndVariantName(data, productTitle, variantName))
        || (productId && getHelpsWithForProduct(data, productId))
        || (variantName && getHelpsWithByName(data, variantName));
  }

  // ── Dynamic wheel sector rendering ──────────────────────────────────────────
  // Ring radii: outer radius 238 split into 10 equal rings (238/10 × n).
  var WHEEL_RING_RADII = [23.8, 47.6, 71.4, 95.2, 119, 142.8, 166.6, 190.4, 214.2, 238];
  // Number of rings filled for helps_with position 0 (1st), 1 (2nd), 2 (3rd), 3 (4th).
  var WHEEL_RING_LEVELS = [10, 8, 6, 4];
  // Default fill — overridden per-section via data-wheel-fill (set by Liquid based on THC/CBD).
  var WHEEL_FILL_DEFAULT = '#CFEE8E';

  // Build an SVG arc path for a pie-wedge sector.
  // svgAngleDeg: center angle in SVG convention (0=right, CW positive, degrees).
  // spreadDeg: total angular width of the sector (60° for 1/6 of the circle).
  function wheelSectorPath(cx, cy, r, svgAngleDeg, spreadDeg) {
    var half = spreadDeg / 2;
    var saRad = (svgAngleDeg - half) * Math.PI / 180;
    var eaRad = (svgAngleDeg + half) * Math.PI / 180;
    var x1 = (cx + r * Math.cos(saRad)).toFixed(3);
    var y1 = (cy + r * Math.sin(saRad)).toFixed(3);
    var x2 = (cx + r * Math.cos(eaRad)).toFixed(3);
    var y2 = (cy + r * Math.sin(eaRad)).toFixed(3);
    // large-arc-flag = 0 because each sector is 60° (< 180°); sweep-flag = 1 (CW).
    return 'M ' + cx + ' ' + cy + ' L ' + x1 + ' ' + y1 +
           ' A ' + r + ' ' + r + ' 0 0 1 ' + x2 + ' ' + y2 + ' Z';
  }

  // Re-draw the green sector fills based on the current variant's helps_with array.
  // Animates: existing sectors shrink out (0.2 s), then new sectors grow in (0.38 s spring).
  // Timer is stored on the <g> element so each wheel section manages its own state.
  var WHEEL_EXIT_MS = 230; // matches hdif-sector-out duration + tiny buffer

  function renderWheel(section, helpsWith) {
    var group = section.querySelector('.how-does-it-feel-soul__wheel-sectors');
    if (!group) return;

    // Build and append new sector <path> elements; CSS `hdif-sector-in` fires automatically.
    function buildSectors() {
      while (group.firstChild) group.removeChild(group.firstChild);
      if (!helpsWith || !helpsWith.length) return;

      var posMap = {};
      helpsWith.forEach(function (tag, pos) {
        posMap[tag.toLowerCase().trim()] = pos;
      });

      var wheelFill = section.dataset.wheelFill || WHEEL_FILL_DEFAULT;
      var labels = section.querySelectorAll('.how-does-it-feel-soul__label');
      labels.forEach(function (label) {
        var effectIdx = parseInt(label.dataset.effectIndex, 10);
        var effectKey = (label.dataset.effectKey || label.textContent.trim()).toLowerCase();

        if (!posMap.hasOwnProperty(effectKey)) return;
        var priority = posMap[effectKey];
        if (priority >= WHEEL_RING_LEVELS.length) return;

        var ringCount = WHEEL_RING_LEVELS[priority];
        var fillR = WHEEL_RING_RADII[ringCount - 1];
        // SVG sector center angle: i × 60 + 90 (CSS label angle + 270).
        var svgAngle = effectIdx * 60 + 90;

        var path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
        path.setAttribute('d', wheelSectorPath(240, 240, fillR, svgAngle, 60));
        path.setAttribute('fill', wheelFill);
        group.appendChild(path);
      });
    }

    // Cancel any in-flight exit timer so rapid switches don't queue up.
    if (group._wheelTimer) {
      clearTimeout(group._wheelTimer);
      group._wheelTimer = null;
    }

    var existing = Array.from(group.children);
    if (existing.length === 0) {
      buildSectors(); // nothing to animate out — grow straight in
    } else {
      // Trigger CSS shrink-out on current sectors, then swap to new ones.
      existing.forEach(function (p) { p.classList.add('is-leaving'); });
      group._wheelTimer = setTimeout(function () {
        group._wheelTimer = null;
        buildSectors();
      }, WHEEL_EXIT_MS);
    }
  }
  // ────────────────────────────────────────────────────────────────────────────

  // Position the popup container outside the wheel, beside the given label on desktop.
  function positionPopupNearLabel(section, label) {
    if (window.innerWidth < 750) return;
    var popupsEl = section.querySelector('.how-does-it-feel-soul__popups');
    var wrapEl   = section.querySelector('.how-does-it-feel-soul__wheel-wrap');
    if (!popupsEl || !wrapEl || !label) return;

    var wrapRect  = wrapEl.getBoundingClientRect();
    var labelRect = label.getBoundingClientRect();
    var activePopup = popupsEl.querySelector('.how-does-it-feel-soul__popup--active');
    var popupW = popupsEl.offsetWidth || 280;
    var popupH = activePopup ? activePopup.offsetHeight : (popupsEl.offsetHeight || 240);
    var gap = 16;

    // Label center relative to wheel-wrap
    var lx = labelRect.left + labelRect.width / 2 - wrapRect.left;
    var ly = labelRect.top  + labelRect.height / 2 - wrapRect.top;

    // Place popup on the side of the wheel the label is on (away from wheel center)
    // so it never overlaps the radar circle.
    var left;
    if (lx >= wrapRect.width / 2) {
      // Label is on the right half → popup goes to the right of the label
      left = lx + labelRect.width / 2 + gap;
      // Clamp: don't overflow right edge of viewport
      left = Math.min(left, window.innerWidth - wrapRect.left - popupW - 8);
    } else {
      // Label is on the left half → popup goes to the left of the label
      left = lx - labelRect.width / 2 - popupW - gap;
      // Clamp: don't overflow left edge of viewport
      left = Math.max(left, 8 - wrapRect.left);
    }

    // Center popup vertically on the label, clamped within wrap.
    var top = ly - popupH / 2;
    top = Math.max(0, Math.min(top, Math.max(0, wrapRect.height - popupH)));

    popupsEl.style.right = 'auto';
    popupsEl.style.left  = left + 'px';
    popupsEl.style.top   = top  + 'px';
  }

  // Activate the wheel label + popup that matches helpsWith[0] (the first tag).
  function highlightEffects(section, helpsWith) {
    var labels = section.querySelectorAll('.how-does-it-feel-soul__label');
    var popups = section.querySelectorAll('.how-does-it-feel-soul__popup');

    labels.forEach(function (l) { l.classList.remove('how-does-it-feel-soul__label--active'); });
    popups.forEach(function (p) { p.classList.remove('how-does-it-feel-soul__popup--active'); });

    if (!helpsWith || !helpsWith.length) return;

    // Match the wheel label to the FIRST item in helps_with, not just any item.
    var firstTag = helpsWith[0].toLowerCase().trim();
    var activeLabel = null;

    labels.forEach(function (label, index) {
      var key = (label.dataset.effectKey || label.textContent.trim()).toLowerCase();
      if (key === firstTag) {
        label.classList.add('how-does-it-feel-soul__label--active');
        if (popups[index]) popups[index].classList.add('how-does-it-feel-soul__popup--active');
        activeLabel = label;
      }
    });

    renderWheel(section, helpsWith);
    positionPopupNearLabel(section, activeLabel);
  }

  // Sync the active dose tab to the given variant, then highlight its effects.
  // optionValue: the variant's value for the displayed option (e.g. "Single", "500mg").
  function applyVariant(section, variantId, optionValue) {
    var flavors = section.querySelectorAll('.how-does-it-feel-soul__flavor[data-variant-id]');
    if (!flavors.length) return; // static flavor blocks — skip variant logic

    flavors.forEach(function (f) { f.classList.remove('how-does-it-feel-soul__flavor--active'); });

    // Prefer matching by option value (de-duped buttons), fall back to exact variant ID.
    var match = null;
    if (optionValue) {
      match = section.querySelector('.how-does-it-feel-soul__flavor[data-option-value="' + optionValue + '"]');
    }
    if (!match) {
      match = section.querySelector('.how-does-it-feel-soul__flavor[data-variant-id="' + variantId + '"]');
    }
    if (match) match.classList.add('how-does-it-feel-soul__flavor--active');

    var productId    = section.dataset.productId;
    var productTitle = section.dataset.productTitle;
    loadData().then(function (data) {
      var helpsWith = resolveHelpsWith(data, variantId, productId, productTitle, optionValue);
      if (helpsWith) highlightEffects(section, helpsWith);
    });
  }

  function initSection(section) {
    var labels = section.querySelectorAll('.how-does-it-feel-soul__label');
    var popups = section.querySelectorAll('.how-does-it-feel-soul__popup');
    var flavors = section.querySelectorAll('.how-does-it-feel-soul__flavor');
    var isDynamic = section.querySelector('.how-does-it-feel-soul__flavor[data-variant-id]') !== null;

    // Effect label click — show corresponding popup
    labels.forEach(function (label, index) {
      label.addEventListener('click', function () {
        labels.forEach(function (l) { l.classList.remove('how-does-it-feel-soul__label--active'); });
        popups.forEach(function (p) { p.classList.remove('how-does-it-feel-soul__popup--active'); });
        label.classList.add('how-does-it-feel-soul__label--active');
        if (popups[index]) popups[index].classList.add('how-does-it-feel-soul__popup--active');
        positionPopupNearLabel(section, label);
      });
      label.addEventListener('keydown', function (e) {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); label.click(); }
      });
    });

    // Flavor / variant tab click
    flavors.forEach(function (flavor) {
      flavor.addEventListener('click', function () {
        flavors.forEach(function (f) { f.classList.remove('how-does-it-feel-soul__flavor--active'); });
        flavor.classList.add('how-does-it-feel-soul__flavor--active');

        var pid          = section.dataset.productId;
        var productTitle = section.dataset.productTitle;
        var variantName  = (flavor.dataset.optionValue || flavor.textContent).trim();
        loadData().then(function (data) {
          var helpsWith = resolveHelpsWith(data, flavor.dataset.variantId, pid, productTitle, variantName);
          if (helpsWith) highlightEffects(section, helpsWith);
        });
      });
    });

    // On load: highlight effects for the initially selected flavor/variant.
    // Falls back to the URL ?variant= param or the section's data-variant-id
    // attribute for products that have no dose tabs (e.g. flavor-only products).
    var activeTab    = section.querySelector('.how-does-it-feel-soul__flavor--active');
    var urlVariantId = new URLSearchParams(window.location.search).get('variant');
    var pid          = section.dataset.productId;
    var productTitle = section.dataset.productTitle;

    if (activeTab) {
      var initialVariantId = activeTab.dataset.variantId || urlVariantId;
      var variantName      = (activeTab.dataset.optionValue || activeTab.textContent).trim();

      loadData().then(function (data) {
        var helpsWith = resolveHelpsWith(data, initialVariantId, pid, productTitle, variantName);
        if (urlVariantId) applyVariant(section, urlVariantId);
        if (helpsWith) highlightEffects(section, helpsWith);
      });
    } else {
      // No dose tabs (flavor-only / single-dose product).
      // Try: URL variant ID → section's initial variant ID → product ID fallback.
      var fallbackId = urlVariantId || section.dataset.initialVariantId;
      loadData().then(function (data) {
        var helpsWith = resolveHelpsWith(data, fallbackId, pid, productTitle, null);
        if (helpsWith) highlightEffects(section, helpsWith);
      });
    }

    // Position popup at the initially active label (set by Liquid before JS runs).
    var initialActiveLabel = section.querySelector('.how-does-it-feel-soul__label--active');
    if (initialActiveLabel) positionPopupNearLabel(section, initialActiveLabel);

    // Reposition on resize (debounced).
    var _resizeTimer;
    window.addEventListener('resize', function () {
      clearTimeout(_resizeTimer);
      _resizeTimer = setTimeout(function () {
        var currentActive = section.querySelector('.how-does-it-feel-soul__label--active');
        positionPopupNearLabel(section, currentActive);
      }, 100);
    });

    // Sync with the main product variant picker (via pub/sub).
    // Always subscribe when on a product page — not just when dose tabs are present.
    // This ensures products whose option names don't match dose keywords (e.g. Formula:
    // Uplift/Balance/Mellow) still update the wheel when the variant picker changes.
    var isProductPage = !!section.dataset.productId;
    if (isProductPage && typeof subscribe !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.variantChange, function (event) {
        var variant = event && event.data && event.data.variant;
        if (variant && variant.id) {
          // Determine which option value to highlight based on data-option-index.
          var container = section.querySelector('.how-does-it-feel-soul__flavors[data-option-index]');
          if (container) {
            // Product has dose tabs — sync active tab and wheel.
            var optionIndex = parseInt(container.dataset.optionIndex, 10);
            var optionValue = variant['option' + (optionIndex + 1)];
            applyVariant(section, variant.id, optionValue);
          } else {
            // Flavor-only product (no dose tabs) — just update the wheel.
            loadData().then(function (data) {
              var helpsWith = resolveHelpsWith(data, variant.id, section.dataset.productId, section.dataset.productTitle, null);
              if (helpsWith) highlightEffects(section, helpsWith);
            });
          }
        }
      });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      document.querySelectorAll('.how-does-it-feel-soul').forEach(initSection);
    });
  } else {
    document.querySelectorAll('.how-does-it-feel-soul').forEach(initSection);
  }

  document.addEventListener('shopify:section:load', function (e) {
    var section = e.target.querySelector('.how-does-it-feel-soul');
    if (section) initSection(section);
  });
})();
